/**
 * ✅ FERP TEMPLATE REFINEMENT EDITOR
 *
 * Sprint 8 LLM A Track - Day 5
 *
 * Template Refinement Editor for Low-Confidence Fields
 * - Field-by-field refinement interface
 * - Evidence attribution display
 * - Multi-LLM disagreement visualization
 * - Justification capture
 * - Before/after diff view
 * - Confidence impact preview
 * - Batch refinement submission
 *
 * ARCHITECTURE:
 * - Mantine UI only
 * - Data-driven (all data from useTemplateRefinement hook)
 * - Multi-tenant isolation
 * - Immutable refinement audit trail
 *
 * CONFIDENCE THRESHOLDS:
 * - >= 0.90: Green (high confidence)
 * - 0.85-0.89: Yellow (medium confidence)
 * - 0.70-0.84: Orange (low confidence - review recommended)
 * - < 0.70: Red (critical - expert review required)
 *
 * USAGE:
 * ```tsx
 * <TemplateRefinementEditor
 *   templateId="tpl-123"
 *   candidates={refinementCandidates}
 *   onSubmit={(result) => console.log('Refined:', result)}
 * />
 * ```
 */
// @ts-nocheck


import React, { useState } from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  Badge,
  Button,
  TextInput,
  Textarea,
  Alert,
  Table,
  Accordion,
  Tabs,
  Code,
  ThemeIcon,
  Progress,
  ActionIcon,
  Tooltip,
  Title,
  Grid,
  Divider,
} from '@mantine/core';
import {
  IconEdit,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconFileCheck,
  IconBrain,
  IconQuote,
  IconHistory,
  IconDeviceFloppy,
  IconRefresh,
  IconArrowRight,
} from '@tabler/icons-react';
import {
  useTemplateRefinement,
  useRefinementCandidates,
  type RefinementCandidate,
  type RefinementResult,
} from './hooks/useTemplateRefinement';
import { notifications } from '@mantine/notifications';

// ============================================================
// Component Props
// ============================================================

export interface TemplateRefinementEditorProps {
  /**
   * Template ID to refine
   */
  templateId: string;

  /**
   * Refinement candidates (low-confidence fields)
   */
  candidates?: RefinementCandidate[];

  /**
   * Callback when refinements are submitted successfully
   */
  onSubmit?: (result: RefinementResult) => void;

  /**
   * Callback when refinement fails
   */
  onError?: (error: Error) => void;

  /**
   * Show evidence attribution (default: true)
   */
  showEvidence?: boolean;

  /**
   * Show LLM disagreements (default: true)
   */
  showLLMDisagreements?: boolean;

  /**
   * Compact view (hides details)
   */
  compact?: boolean;

  /**
   * Read-only mode (view refinements without editing)
   */
  readOnly?: boolean;
}

// ============================================================
// Main Component
// ============================================================

export const TemplateRefinementEditor: React.FC<TemplateRefinementEditorProps> = ({
  templateId,
  candidates: providedCandidates,
  onSubmit,
  onError,
  showEvidence = true,
  showLLMDisagreements = true,
  compact = false,
  readOnly = false,
}) => {
  const {
    pendingRefinements,
    refinementHistory,
    isRefining,
    error,
    refineField,
    submitRefinements,
    cancelRefinement,
    clearPendingRefinements,
    hasPendingRefinement,
    getPendingRefinement,
    getFieldHistory,
    hasPendingChanges,
    pendingCount,
    totalRefinements,
  } = useTemplateRefinement(templateId);

  const {
    candidates: fetchedCandidates,
    loading: candidatesLoading,
    fetchCandidates,
    requiresReview,
    criticalCount,
  } = useRefinementCandidates(templateId);

  const candidates = providedCandidates || fetchedCandidates;

  const [reviewNotes, setReviewNotes] = useState('');
  const [activeTab, setActiveTab] = useState<string>('refinement');

  /**
   * Handle refinement submission
   */
  const handleSubmit = async () => {
    try {
      const result = await submitRefinements(reviewNotes);

      if (onSubmit) {
        onSubmit(result);
      }

      setReviewNotes('');

      notifications.show({
        title: 'Refinements Submitted',
        message: `${result.refinedFields.length} field(s) updated successfully`,
        color: 'green',
      });
    } catch (err) {
      const submitError = err instanceof Error ? err : new Error('Submission failed');
      if (onError) {
        onError(submitError);
      }
    }
  };

  /**
   * Handle field refinement
   */
  const handleRefineField = async (
    candidate: RefinementCandidate,
    refinedValue: any,
    justification: string
  ) => {
    try {
      await refineField(
        candidate.fieldPath,
        refinedValue,
        justification,
        candidate.evidenceReferences.map((ref) => ref.sourceDocument)
      );
    } catch (err) {
      console.error('Failed to refine field:', err);
    }
  };

  if (!templateId) {
    return (
      <Alert color="blue" icon={<IconAlertCircle size={16} />}>
        No template selected for refinement
      </Alert>
    );
  }

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group justify="apart">
          <Group gap="xs">
            <ThemeIcon size="lg" variant="light" color="blue">
              <IconEdit size={20} />
            </ThemeIcon>
            <div>
              <Title order={4}>Template Refinement Editor</Title>
              <Text size="sm" c="dimmed">
                Review and refine low-confidence fields from AI extraction
              </Text>
            </div>
          </Group>

          {/* Status badges */}
          <Group gap="xs">
            {hasPendingChanges && (
              <Badge color="orange" variant="light">
                {pendingCount} Pending
              </Badge>
            )}
            {totalRefinements > 0 && (
              <Badge color="green" variant="light">
                {totalRefinements} Refined
              </Badge>
            )}
            {criticalCount > 0 && (
              <Badge color="red" variant="light">
                {criticalCount} Critical
              </Badge>
            )}
          </Group>
        </Group>

        {/* Error state */}
        {error && (
          <Alert color="red" icon={<IconX size={16} />} title="Refinement Error">
            {error.message}
          </Alert>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'refinement')}>
          <Tabs.List>
            <Tabs.Tab value="refinement" leftSection={<IconEdit size={16} />}>
              Field Refinement ({candidates.length})
            </Tabs.Tab>
            <Tabs.Tab value="history" leftSection={<IconHistory size={16} />}>
              Refinement History ({totalRefinements})
            </Tabs.Tab>
          </Tabs.List>

          {/* Refinement Tab */}
          <Tabs.Panel value="refinement" pt="md">
            <Stack gap="md">
              {candidates.length === 0 ? (
                <Alert color="green" icon={<IconFileCheck size={16} />}>
                  All fields have high confidence. No refinement needed.
                </Alert>
              ) : (
                <>
                  {/* Refinement candidates */}
                  <Accordion variant="separated">
                    {candidates.map((candidate) => (
                      <RefinementCandidateItem
                        key={candidate.fieldPath}
                        candidate={candidate}
                        onRefine={handleRefineField}
                        isPending={hasPendingRefinement(candidate.fieldPath)}
                        pendingRefinement={getPendingRefinement(candidate.fieldPath)}
                        onCancel={() => cancelRefinement(candidate.fieldPath)}
                        showEvidence={showEvidence}
                        showLLMDisagreements={showLLMDisagreements}
                        compact={compact}
                        readOnly={readOnly}
                      />
                    ))}
                  </Accordion>

                  {/* Review notes */}
                  {!readOnly && hasPendingChanges && (
                    <Card withBorder padding="sm">
                      <Stack gap="xs">
                        <Text size="sm" fw={500}>
                          Review Notes (Optional)
                        </Text>
                        <Textarea
                          placeholder="Add notes about this review session..."
                          value={reviewNotes}
                          onChange={(e) => setReviewNotes(e.target.value)}
                          minRows={2}
                          maxRows={4}
                        />
                      </Stack>
                    </Card>
                  )}

                  {/* Action buttons */}
                  {!readOnly && (
                    <Group justify="apart">
                      <Button
                        variant="subtle"
                        color="red"
                        onClick={clearPendingRefinements}
                        disabled={!hasPendingChanges || isRefining}
                        leftSection={<IconRefresh size={16} />}
                      >
                        Clear Pending ({pendingCount})
                      </Button>
                      <Button
                        color="blue"
                        onClick={handleSubmit}
                        disabled={!hasPendingChanges || isRefining}
                        loading={isRefining}
                        leftSection={<IconDeviceFloppy size={16} />}
                      >
                        Submit Refinements ({pendingCount})
                      </Button>
                    </Group>
                  )}
                </>
              )}
            </Stack>
          </Tabs.Panel>

          {/* History Tab */}
          <Tabs.Panel value="history" pt="md">
            <Stack gap="md">
              {refinementHistory.length === 0 ? (
                <Alert color="blue" icon={<IconHistory size={16} />}>
                  No refinement history yet
                </Alert>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Field</Table.Th>
                      <Table.Th>Original → Refined</Table.Th>
                      <Table.Th>Confidence Impact</Table.Th>
                      <Table.Th>Justification</Table.Th>
                      <Table.Th>Refined By</Table.Th>
                      <Table.Th>Date</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {refinementHistory.map((entry, idx) => (
                      <RefinementHistoryRow key={idx} entry={entry} />
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Card>
  );
};

// ============================================================
// Sub-Components
// ============================================================

interface RefinementCandidateItemProps {
  candidate: RefinementCandidate;
  onRefine: (candidate: RefinementCandidate, refinedValue: any, justification: string) => void;
  isPending: boolean;
  pendingRefinement?: any;
  onCancel: () => void;
  showEvidence: boolean;
  showLLMDisagreements: boolean;
  compact: boolean;
  readOnly: boolean;
}

const RefinementCandidateItem: React.FC<RefinementCandidateItemProps> = ({
  candidate,
  onRefine,
  isPending,
  pendingRefinement,
  onCancel,
  showEvidence,
  showLLMDisagreements,
  compact,
  readOnly,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [refinedValue, setRefinedValue] = useState(candidate.currentValue || '');
  const [justification, setJustification] = useState('');

  const confidenceColor = getConfidenceColor(candidate.confidence);
  const criticalityColor = getCriticalityColor(candidate.criticality);

  const handleSave = () => {
    if (!refinedValue || !justification) {
      notifications.show({
        title: 'Incomplete Refinement',
        message: 'Both refined value and justification are required',
        color: 'orange',
      });
      return;
    }

    onRefine(candidate, refinedValue, justification);
    setIsEditing(false);
    setJustification('');
  };

  const handleCancel = () => {
    setRefinedValue(candidate.currentValue || '');
    setJustification('');
    setIsEditing(false);
    if (isPending) {
      onCancel();
    }
  };

  return (
    <Accordion.Item value={candidate.fieldPath}>
      <Accordion.Control
        icon={
          <ThemeIcon color={isPending ? 'orange' : criticalityColor} variant="light" size="sm">
            {isPending ? <IconEdit size={14} /> : <IconAlertCircle size={14} />}
          </ThemeIcon>
        }
      >
        <Group justify="apart">
          <div>
            <Text size="sm" fw={500}>
              {candidate.fieldPath}
            </Text>
            <Text size="xs" c="dimmed">
              Current: {truncate(String(candidate.currentValue), 50)}
            </Text>
          </div>
          <Group gap="xs">
            <Badge color={confidenceColor} size="sm">
              {Math.round(candidate.confidence * 100)}% Confidence
            </Badge>
            <Badge color={criticalityColor} size="sm">
              {candidate.criticality}
            </Badge>
            {isPending && (
              <Badge color="orange" size="sm">
                Pending
              </Badge>
            )}
          </Group>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap="md">
          {/* Current value & confidence */}
          <Grid>
            <Grid.Col span={8}>
              <Stack gap="xs">
                <Text size="xs" c="dimmed">
                  Current Value:
                </Text>
                <Code block>{JSON.stringify(candidate.currentValue, null, 2)}</Code>
              </Stack>
            </Grid.Col>
            <Grid.Col span={4}>
              <Stack ta="center" gap="xs">
                <Text size="xs" c="dimmed">
                  Confidence
                </Text>
                <Progress
                  value={candidate.confidence * 100}
                  size="xl"
                  color={confidenceColor}
                  style={{ width: '100%' }}
                />
                <Text size="lg" fw={700} c={confidenceColor}>
                  {Math.round(candidate.confidence * 100)}%
                </Text>
              </Stack>
            </Grid.Col>
          </Grid>

          {/* LLM disagreements */}
          {showLLMDisagreements && candidate.llmResults.length > 1 && (
            <Card withBorder padding="xs">
              <Stack gap="xs">
                <Group gap="xs">
                  <IconBrain size={14} />
                  <Text size="xs" fw={500}>
                    LLM Disagreements
                  </Text>
                </Group>
                <Table>
                  <Table.Tbody>
                    {candidate.llmResults.map((result) => (
                      <Table.Tr key={result.provider}>
                        <Table.Td>
                          <Badge size="xs">{result.provider}</Badge>
                        </Table.Td>
                        <Table.Td>
                          <Code>{truncate(String(result.extractedValue), 30)}</Code>
                        </Table.Td>
                        <Table.Td>
                          <Badge color={getConfidenceColor(result.confidence)} size="xs">
                            {Math.round(result.confidence * 100)}%
                          </Badge>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Stack>
            </Card>
          )}

          {/* Evidence references */}
          {showEvidence && candidate.evidenceReferences.length > 0 && (
            <Card withBorder padding="xs">
              <Stack gap="xs">
                <Group gap="xs">
                  <IconQuote size={14} />
                  <Text size="xs" fw={500}>
                    Evidence from Source Document
                  </Text>
                </Group>
                {candidate.evidenceReferences.slice(0, 3).map((ref, idx) => (
                  <Alert key={idx} color="blue" variant="light" p="xs">
                    <Stack gap="4px">
                      <Text size="xs" fw={500}>
                        Page {ref.pageNumber} (Relevance: {Math.round(ref.relevanceScore * 100)}%)
                      </Text>
                      <Text size="xs" c="dimmed" italic>
                        &quot;{truncate(ref.excerpt, 120)}&quot;
                      </Text>
                    </Stack>
                  </Alert>
                ))}
              </Stack>
            </Card>
          )}

          <Divider />

          {/* Refinement editor */}
          {!readOnly && (
            <Stack gap="xs">
              <TextInput
                label="Refined Value"
                placeholder="Enter refined value..."
                value={refinedValue}
                onChange={(e) => setRefinedValue(e.target.value)}
                disabled={isPending && !isEditing}
                rightSection={
                  isPending && !isEditing ? (
                    <ThemeIcon color="orange" size="sm" variant="light">
                      <IconCheck size={14} />
                    </ThemeIcon>
                  ) : null
                }
              />
              <Textarea
                label="Justification (Required)"
                placeholder="Explain why this refinement is needed..."
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                minRows={2}
                disabled={isPending && !isEditing}
              />
              <Group justify="flex-end" gap="xs">
                {(isEditing || !isPending) && (
                  <>
                    <Button variant="subtle" size="xs" onClick={handleCancel}>
                      Cancel
                    </Button>
                    <Button size="xs" onClick={handleSave} leftSection={<IconCheck size={14} />}>
                      {isPending ? 'Update Refinement' : 'Add to Pending'}
                    </Button>
                  </>
                )}
                {isPending && !isEditing && (
                  <>
                    <Button variant="subtle" size="xs" color="red" onClick={handleCancel}>
                      Remove from Pending
                    </Button>
                    <Button variant="subtle" size="xs" onClick={() => setIsEditing(true)}>
                      Edit
                    </Button>
                  </>
                )}
              </Group>
            </Stack>
          )}
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
};

interface RefinementHistoryRowProps {
  entry: any;
}

const RefinementHistoryRow: React.FC<RefinementHistoryRowProps> = ({ entry }) => {
  return (
    <Table.Tr>
      <Table.Td>
        <Code>{entry.fieldPath}</Code>
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          <Text size="sm" c="dimmed" style={{ maxWidth: '100px' }} truncate>
            {String(entry.originalValue)}
          </Text>
          <IconArrowRight size={14} />
          <Text size="sm" fw={500} style={{ maxWidth: '100px' }} truncate>
            {String(entry.refinedValue)}
          </Text>
        </Group>
      </Table.Td>
      <Table.Td>
        <Badge color={entry.confidenceChange > 0 ? 'green' : 'red'}>
          {entry.confidenceChange > 0 ? '+' : ''}
          {Math.round(entry.confidenceChange * 100)}%
        </Badge>
      </Table.Td>
      <Table.Td>
        <Tooltip label={entry.justification}>
          <Text size="sm" style={{ maxWidth: '200px' }} truncate>
            {entry.justification}
          </Text>
        </Tooltip>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{entry.refinedBy}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {new Date(entry.timestamp).toLocaleDateString()}
        </Text>
      </Table.Td>
    </Table.Tr>
  );
};

// ============================================================
// Helper Functions
// ============================================================

function getConfidenceColor(confidence: number): string {
  if (confidence >= 0.9) return 'green';
  if (confidence >= 0.85) return 'lime';
  if (confidence >= 0.7) return 'orange';
  return 'red';
}

function getCriticalityColor(criticality: string): string {
  switch (criticality) {
    case 'CRITICAL':
      return 'red';
    case 'HIGH':
      return 'orange';
    case 'MEDIUM':
      return 'yellow';
    case 'LOW':
      return 'green';
    default:
      return 'gray';
  }
}

function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength) + '...';
}
